import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {clearCommentPendingDeleteAtom, markCommentPendingDeleteAtom} from 'atoms/comment';
import {
  Comment,
  CommentService,
  DocumentService,
  DocumentThread,
  DocumentThreadService,
} from 'client';
import {useCollabId} from 'components/DSYS/CollabProvider';
import {$isHighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import {sanitizeCommentContent} from 'helpers/commentContent';
import {useSetAtom} from 'jotai';
import {$getRoot, $isElementNode, LexicalNode} from 'lexical';
import {getService} from 'utilities';

import {CreateThreadParams, useThreadsContext} from './ThreadsProvider';

/**
 * Hook to access threads from the `DocumentThreadsProvider` context.
 *
 * Returns empty state if no provider is present (graceful fallback).
 */
export const useThreads = (): {
  threads: DocumentThread[] | undefined;
  isLoading: boolean;
  refetch?: () => void;
  createThread?: (params: CreateThreadParams) => Promise<string>;
} => {
  const context = useThreadsContext();

  return {
    threads: context?.threads,
    isLoading: context?.isLoading ?? false,
    refetch: context?.refetch,
    createThread: context?.createThread,
  };
};

/**
 * Hook that gets document threads without requiring CollabContext.
 * Use this when you need to fetch threads outside of a CollabProvider.
 * @param documentId - The editor document ID to fetch threads for
 */
export const useThreadsWithoutContext = (documentId?: string) => {
  const {data: threads, isLoading} = useQuery({
    queryKey: ['document', 'threads', documentId],
    queryFn: ({signal}) => {
      const request = getService(DocumentService).getThreadsApiV1DocumentDocumentIdThreadsGet(
        documentId!
      );
      signal.addEventListener('abort', () => {
        request.cancel();
      });
      return request;
    },
    enabled: Boolean(documentId),
    refetchOnWindowFocus: 'always',
    refetchOnReconnect: true,
  });

  return {threads, isLoading};
};

const getSaveableCommentContent = (content: string) => {
  const sanitizedContent = sanitizeCommentContent(content);
  if (!sanitizedContent) {
    throw new Error('comment content is required');
  }
  return sanitizedContent;
};

/**
 * Hook that automatically gets collaboration presence/users for the current session.
 */
export const useDocumentThreadService = () => {
  const queryClient = useQueryClient();
  const collabId = useCollabId();

  const onSettled = (response: DocumentThread | undefined) => {
    if (!response) {
      return;
    }
    const responseDocumentId = response.document_id ?? collabId;

    // Update the specific thread in cache
    queryClient.setQueryData(['document', 'thread', response.id], response);

    // Invalidate the threads list to refresh
    if (responseDocumentId) {
      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', responseDocumentId],
      });
    }
  };

  const {mutateAsync: updateThread} = useMutation({
    mutationFn: ({threadId, data}: {threadId: string; data: Partial<DocumentThread>}) =>
      getService(DocumentThreadService).updateApiV1DocumentthreadPut({
        ...data,
        id: threadId,
      } as any),
    onMutate: () => ({type: 'update'}),
    onSettled,
  });

  return {updateThread};
};

/**
 * Helper function to unwrap highlight nodes for a given thread ID.
 */
const unwrapHighlightNodesForThread = (editor: any, threadId: string) => {
  editor.update(
    () => {
      const root = $getRoot();

      // Recursively find all highlight nodes with this thread ID and unwrap them
      const findAndUnwrapNodes = (node: LexicalNode) => {
        if ($isHighlightNode(node) && node.hasHighlightId(threadId)) {
          // For HighlightNode (ElementNode), extract children and replace the node
          const children = node.getChildren();
          children.forEach((child: LexicalNode) => {
            node.insertBefore(child);
          });
          node.remove();
        } else if ($isElementNode(node)) {
          node.getChildren().forEach(findAndUnwrapNodes);
        }
      };

      root.getChildren().forEach(findAndUnwrapNodes);
    },
    {discrete: true} // Ensures the update completes synchronously
  );
};

const reinsertDeletedComment = (
  comments: Comment[] | undefined,
  deletedComment: Comment,
  deletedCommentIndex: number
) => {
  const currentComments = comments ?? [];

  if (currentComments.some((comment) => comment.id === deletedComment.id)) {
    return currentComments;
  }

  const nextComments = [...currentComments];
  const insertAt = Math.min(Math.max(deletedCommentIndex, 0), nextComments.length);
  nextComments.splice(insertAt, 0, deletedComment);
  return nextComments;
};

const getCurrentDocumentThread = (
  queryClient: ReturnType<typeof useQueryClient>,
  documentId: string | undefined,
  threadId: string
) => {
  const currentThread = queryClient.getQueryData(['document', 'thread', threadId]) as
    | DocumentThread
    | undefined;
  if (currentThread) {
    return currentThread;
  }

  return (
    queryClient.getQueryData(['document', 'threads', documentId]) as DocumentThread[] | undefined
  )?.find((thread) => thread.id === threadId);
};

/**
 * Hook for managing document comment operations (create, update, delete)
 */
export const useDocumentCommentService = () => {
  const queryClient = useQueryClient();
  const documentId = useCollabId();
  const [editor] = useLexicalComposerContext();
  const markCommentPendingDelete = useSetAtom(markCommentPendingDeleteAtom);
  const clearCommentPendingDelete = useSetAtom(clearCommentPendingDeleteAtom);

  const {mutateAsync: deleteComment} = useMutation({
    mutationFn: ({commentId}: {commentId: string}) =>
      getService(CommentService).deleteCommentApiV1CommentCommentIdDelete(commentId),
    onMutate: async ({commentId}) => {
      await queryClient.cancelQueries({
        queryKey: ['document', 'threads', documentId],
      });

      const previousThreads = queryClient.getQueryData(['document', 'threads', documentId]) as
        | DocumentThread[]
        | undefined;
      const threadWithDeletedComment = previousThreads?.find((thread) =>
        thread.comments?.some((comment) => comment.id === commentId)
      );

      if (!threadWithDeletedComment) {
        return {
          threadId: undefined,
          deletedComment: undefined,
          deletedCommentIndex: -1,
          shouldOptimisticallyRemove: false,
        };
      }

      const deletedCommentIndex =
        threadWithDeletedComment.comments?.findIndex((comment) => comment.id === commentId) ?? -1;
      const deletedComment =
        deletedCommentIndex >= 0
          ? threadWithDeletedComment.comments?.[deletedCommentIndex]
          : undefined;
      const wasLastActiveComment =
        (threadWithDeletedComment.comments?.filter(
          (comment) => comment.id !== commentId && !comment.is_deleted
        ).length ?? 0) === 0;
      const shouldOptimisticallyRemove = Boolean(deletedComment && !wasLastActiveComment);

      if (deletedComment && wasLastActiveComment) {
        markCommentPendingDelete(deletedComment.id);
      }

      await queryClient.cancelQueries({
        queryKey: ['document', 'thread', threadWithDeletedComment.id],
      });

      if (shouldOptimisticallyRemove) {
        const updatedThread = {
          ...threadWithDeletedComment,
          comments: threadWithDeletedComment.comments?.filter(
            (comment) => comment.id !== commentId
          ),
        };

        queryClient.setQueryData(
          ['document', 'threads', documentId],
          (old: DocumentThread[] | undefined) => {
            return (old ?? []).map((thread) =>
              thread.id === threadWithDeletedComment.id ? updatedThread : thread
            );
          }
        );
        queryClient.setQueryData(
          ['document', 'thread', threadWithDeletedComment.id],
          updatedThread
        );
      }

      return {
        threadId: threadWithDeletedComment.id,
        deletedComment,
        deletedCommentIndex,
        shouldOptimisticallyRemove,
      };
    },
    onError: (_error, _variables, context) => {
      if (!context) {
        return;
      }

      if (context.deletedComment) {
        clearCommentPendingDelete(context.deletedComment.id);
      }

      if (context.shouldOptimisticallyRemove && context.threadId && context.deletedComment) {
        const deletedComment = context.deletedComment;

        queryClient.setQueryData(
          ['document', 'threads', documentId],
          (currentThreads: DocumentThread[] | undefined) =>
            currentThreads?.map((thread) =>
              thread.id === context.threadId
                ? {
                    ...thread,
                    comments: reinsertDeletedComment(
                      thread.comments,
                      deletedComment,
                      context.deletedCommentIndex
                    ),
                  }
                : thread
            )
        );

        queryClient.setQueryData(
          ['document', 'thread', context.threadId],
          (currentThread: DocumentThread | undefined) => {
            if (!currentThread) {
              return currentThread;
            }

            return {
              ...currentThread,
              comments: reinsertDeletedComment(
                currentThread.comments,
                deletedComment,
                context.deletedCommentIndex
              ),
            };
          }
        );
      }

      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', documentId],
      });

      if (context.threadId) {
        queryClient.invalidateQueries({
          queryKey: ['document', 'thread', context.threadId],
        });
      }
    },
    onSuccess: (_, _variables, context) => {
      const threadId = context?.threadId;
      const deletedCommentId = context?.deletedComment?.id;

      if (threadId && deletedCommentId) {
        const currentThread = getCurrentDocumentThread(queryClient, documentId, threadId);
        const shouldUnwrapHighlight =
          (currentThread?.comments?.filter(
            (comment) => comment.id !== deletedCommentId && !comment.is_deleted
          ).length ?? 0) === 0;

        queryClient.setQueryData(
          ['document', 'threads', documentId],
          (currentThreads: DocumentThread[] | undefined) =>
            currentThreads?.map((thread) =>
              thread.id === threadId
                ? {
                    ...thread,
                    comments: thread.comments?.filter((comment) => comment.id !== deletedCommentId),
                  }
                : thread
            )
        );

        queryClient.setQueryData(
          ['document', 'thread', threadId],
          (currentThreadState: DocumentThread | undefined) => {
            if (!currentThreadState) {
              return currentThreadState;
            }

            return {
              ...currentThreadState,
              comments: currentThreadState.comments?.filter(
                (comment) => comment.id !== deletedCommentId
              ),
            };
          }
        );
        clearCommentPendingDelete(deletedCommentId);

        if (shouldUnwrapHighlight) {
          // Only remove the highlight after the delete has been confirmed by the server.
          unwrapHighlightNodesForThread(editor, threadId);

          setTimeout(() => {
            queryClient.invalidateQueries({
              queryKey: ['document', 'threads', documentId],
            });
            queryClient.invalidateQueries({
              queryKey: ['document', 'thread', threadId],
            });
          }, 100);
          return;
        }
      }

      if (deletedCommentId) {
        clearCommentPendingDelete(deletedCommentId);
      }

      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', documentId],
      });

      if (threadId) {
        queryClient.invalidateQueries({
          queryKey: ['document', 'thread', threadId],
        });
      }
    },
  });

  const {mutateAsync: updateComment} = useMutation({
    mutationFn: ({commentId, data}: {commentId: string; data: any}) =>
      getService(CommentService).updateCommentApiV1CommentCommentIdPut(commentId, {
        ...data,
        content: getSaveableCommentContent(data.content),
      }),
    onSuccess: (updatedComment) => {
      // Optimistically update the comment in the thread
      queryClient.setQueryData(
        ['document', 'threads', documentId],
        (old: DocumentThread[] | undefined) => {
          return (old ?? []).map((th) => {
            if (th.id === updatedComment.document_thread_id) {
              return {
                ...th,
                comments: th.comments?.map((c) =>
                  c.id === updatedComment.id ? updatedComment : c
                ),
              };
            }
            return th;
          });
        }
      );

      // Invalidate the threads list to refresh
      queryClient.invalidateQueries({
        queryKey: ['document', 'threads', documentId],
      });
    },
  });

  return {deleteComment, updateComment};
};
